import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Form, notification, Select } from "antd";
import { userProfiles } from "../../../../redux/slices/UserProfilesSlice";
import { checkSpecialDestination } from "../../../../utils/postalCodeUtils";
import resetParticipantFields, {
  getParticipantType,
  transformParticipantsToOptions,
  buildAsyncSelectNotFoundContent,
} from "../../../../utils/participantUtils";
import { canUseSpecialPostCode } from "../../../../utils/userRolesUtils";

const { Option } = Select;

const initialSearchState = {
  participantsList: [],
  options: [],
  value: undefined,
  isLoading: false,
  isSelectOpen: false,
  isPending: false,
  hasSearched: false,
  matchesCount: 0,
};

const makeNewOption = (value, contactType) => ({
  key: `__new__:${value}`,
  label: `${value} (Nuevo ${contactType})`,
});

export default function AsyncParticipantSelect({
  getFieldDecorator,
  getParticipantList = () => {},
  initialValue = undefined,
  label,
  name,
  labelField = "name",
  searchField = "name",
  disabled = false,
  placeholder = "Pulse aquí para mostrar opciones o teclee para buscar",
  requiredMessage = "Campo obligatorio",
  required,
  setFieldsValue,
  handleParticipantDetails,
  contactType = "contacto",
  getFieldsValue,
  proximityService,
  allowCreate = true,
  noResultsText = "No results found",
}) {
  const timeoutRef = useRef(null);
  const { data: reduxUserProfiles } = useSelector(userProfiles);
  const isSender = name?.includes("sender");

  const [search, setSearch] = useState(initialSearchState);

  const handleFocus = async () => {
    if (disabled) return;
    setSearch({
      ...initialSearchState,
      isLoading: true,
      isPending: true,
      isSelectOpen: false,
    });

    const response = await getParticipantList();
    const convertedResponse = transformParticipantsToOptions(response, null, {
      labelField,
      searchField,
      strictLabel: true,
    });

    setSearch((prev) => ({
      ...prev,
      participantsList: response || [],
      options: convertedResponse || [],
      isLoading: false,
      isPending: false,
      hasSearched: false,
      value: null,
      isSelectOpen: (convertedResponse || []).length > 0,
      matchesCount: (convertedResponse || []).length,
    }));
  };

  const handleSearch = async (value = "") => {
    if (disabled) return;
    const trimmed = value.trim();
    setSearch((prev) => ({
      ...initialSearchState,
      value: trimmed,
      isPending: !!trimmed,
      hasSearched: false,
      isSelectOpen: false,
    }));
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (!trimmed) return;

    timeoutRef.current = setTimeout(async () => {
      setSearch((prev) => ({ ...prev, isLoading: true, isPending: true }));

      const response = await getParticipantList(trimmed);
      const matches = transformParticipantsToOptions(response, trimmed, {
        labelField,
        searchField,
        strictLabel: true,
      });
      const options = allowCreate
        ? [makeNewOption(trimmed, contactType), ...matches]
        : matches;

      setSearch((prev) => ({
        ...prev,
        isLoading: false,
        isPending: false,
        hasSearched: true,
        participantsList: response || [],
        options,
        value: trimmed,
        matchesCount: matches.length,
        isSelectOpen: matches.length > 0 || (!!trimmed && matches.length === 0),
      }));
    }, 400);
  };

  const handleSelectOption = (participant) => {
    const setters = { setFieldsValue, setSearch };

    if (!participant) {
      handleParticipantDetails();
      return setSearch(initialSearchState);
    }

    const isNew =
      typeof participant?.key === "string" &&
      participant.key.startsWith("__new__:");
    let participantDetails = undefined;

    if (!isNew) {
      participantDetails = search.participantsList.find(
        (option) => String(option.participantId) === String(participant.key)
      );

      if (participantDetails) {
        const isSpecialPostalCode = checkSpecialDestination(
          participantDetails?.postalCode
        );
        const participantType = getParticipantType(name);
        const canUse = canUseSpecialPostCode(
          getFieldsValue,
          reduxUserProfiles,
          participantType
        );

        if (!canUse && isSpecialPostalCode && !proximityService) {
          resetParticipantFields(name, setters);
          handleParticipantDetails();
          notification.error({
            message: "Acción no permitida",
            description: isSender
              ? `No puede utilizar el código postal ${participantDetails?.postalCode} en el apartado 'Remitente'.`
              : `No tiene autorización para usar el código postal ${participantDetails?.postalCode}. Contacte con Negocio para habilitar el perfil necesario.`,
          });
          return false;
        }
      }
    }

    if (isNew || !participantDetails) {
      resetParticipantFields(name, setters);
      participantDetails = { [name]: participant?.label };
    }

    handleParticipantDetails(participantDetails);

    setSearch((prev) => ({ ...prev, value: participant }));
    setFieldsValue({ [name]: participant });
  };

  const handleChange = (e) => {
    if (e === undefined) {
      handleParticipantDetails(e, name?.includes("Alias") ? "alias" : "name");
      setSearch(initialSearchState);
    }
  };

  return (
    <Form.Item label={label}>
      {getFieldDecorator(name, {
        initialValue: initialValue,
        rules: [{ required, message: requiredMessage }],
      })(
        <Select
          onSearch={handleSearch}
          placeholder={search.isLoading ? "Cargando opciones..." : placeholder}
          showArrow={false}
          defaultActiveFirstOption={false}
          filterOption={false}
          notFoundContent={buildAsyncSelectNotFoundContent(
            search,
            noResultsText
          )}
          showSearch
          optionFilterProp="children"
          onSelect={handleSelectOption}
          labelInValue
          loading={search?.isLoading}
          onFocus={handleFocus}
          onChange={handleChange}
          allowClear
          disabled={disabled}
        >
          {search?.options.map((option) => (
            <Option key={option.key} value={option.key}>
              {option.label}
            </Option>
          ))}
        </Select>
      )}
    </Form.Item>
  );
}
